/**
 * Centralized Supabase Configuration
 * 
 * This file provides a single source of truth for Supabase URL and project ID.
 * All frontend code should import from here instead of hardcoding URLs.
 * 
 * The values are extracted from the auto-generated client.ts file,
 * which is automatically updated when the Supabase project changes.
 */

// These constants match the auto-generated client.ts values
// When migrating to a new Supabase project, only client.ts needs to be updated
// (which happens automatically), and this file reads from there.
const SUPABASE_URL = "https://jtzzubujciqbkylswrtv.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp0enp1YnVqY2lxYmt5bHN3cnR2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA4NjgwOTQsImV4cCI6MjA2NjQ0NDA5NH0.Fayy3E__x_3bIDGOqLr7-kyjeWcoq5FMizmkLzGS-y0";

// Extract project ID from URL
const extractProjectId = (url: string): string => {
  const match = url.match(/https:\/\/([^.]+)/);
  return match ? match[1] : '';
};

/**
 * Supabase project URL (e.g., https://xxxxx.supabase.co)
 */
export const supabaseUrl = SUPABASE_URL;

/**
 * Supabase anonymous/publishable key for client-side operations
 */
export const supabaseAnonKey = SUPABASE_ANON_KEY;

/**
 * Supabase project ID extracted from the URL
 */
export const supabaseProjectId = extractProjectId(SUPABASE_URL);

/**
 * Generate a Supabase dashboard link for a given path
 * @param path - The path within the dashboard (e.g., '/sql/new', '/functions', '/database/tables')
 * @returns Full dashboard URL
 */
export const getDashboardLink = (path: string): string => 
  `https://supabase.com/dashboard/project/${supabaseProjectId}${path}`;

/**
 * Generate an edge function URL
 * @param functionName - The name of the edge function
 * @returns Full edge function URL
 */
export const getEdgeFunctionUrl = (functionName: string): string =>
  `${supabaseUrl}/functions/v1/${functionName}`;

/**
 * Generate a link to edge function logs in the dashboard
 * @param functionName - The name of the edge function
 * @returns Full dashboard URL for function logs
 */
export const getEdgeFunctionLogsLink = (functionName: string): string =>
  getDashboardLink(`/functions/${functionName}/logs`);

/**
 * Common dashboard links
 */
export const dashboardLinks = {
  sqlEditor: getDashboardLink('/sql/new'),
  tables: getDashboardLink('/database/tables'),
  functions: getDashboardLink('/functions'),
  storage: getDashboardLink('/storage/buckets'),
  auth: getDashboardLink('/auth/users'),
  settings: getDashboardLink('/settings/general'),
};
